import { HdZodiacSign } from '../bodygraph/hd-gate-coordinates';

/**
 * De 66 Sides — de Human Design fixed-star-lijst uit Ra's Rave Cosmology
 * (Bhan Tugh). Posities zijn eclipticale lengtes voor het jaar 2000, per ster
 * genoteerd als teken + graden + boogminuten, overgenomen uit de "66 Sides —
 * Human Design Fixed Star Guide" van Naraya Fox (2022) en het IHDS-document
 * "66 Sides by position".
 *
 * Bijzonderheden uit de bronnen (zie ook de notes-flags hieronder):
 *  - Drie side-paren delen exact dezelfde positie onder inwisselbare namen:
 *    #1 Difda & #39 Deneb Kaitos, #15 Polaris & #16 The Pole Star,
 *    #24 Algenubi & #56 Ras Elased Aust.
 *  - #61 Gienah bestaat als twee verschillende sterren met dezelfde naam
 *    (Corvus en Cygnus); beide posities doen mee omdat onbekend is welke
 *    Ra bedoelde.
 *  - #3 Hyades heeft twee sterren (Hyadum I en II); #2 Pleiades wordt door
 *    Alcyone vertegenwoordigd en overspant poort 8.6 t/m 20.1.
 *  - #18 "Pisces" is een constellatienaam; de guide gebruikt Alpherg als
 *    helderste ster van dat sterrenbeeld.
 */

/** Effect-classificatie zoals in het IHDS-positiedocument. */
export type HdFixedStarEffect =
  | 'veryFortunate'
  | 'fortunate'
  | 'neutral'
  | 'unfortunate'
  | 'veryUnfortunate';

/** Positie voor het jaar 2000: teken + graden + boogminuten binnen het teken. */
export interface HdFixedStarPosition {
  readonly sign: HdZodiacSign;
  readonly degree: number;
  readonly minute: number;
  /**
   * Poort.lijn waar deze positie in valt volgens de guide (ter referentie in
   * het info-paneel; wordt niet voor berekeningen gebruikt).
   */
  readonly gateLine: string;
  /** Onderscheidt meerdere posities binnen één side (bv. `hyadum-1`, `corvus`). */
  readonly variantKey?: string;
}

export interface HdFixedStarDefinition {
  /** Side-nummer 1..66 uit de Bhan Tugh-tekening. */
  readonly side: number;
  /** Stabiele key voor i18n (`stars.<key>.*`) en tracking. */
  readonly key: string;
  /** Weergavenaam zoals op de 66-sides-lijst. */
  readonly name: string;
  /** Alternatieve namen/aliassen (bv. de eigenlijke sternaam). */
  readonly altNames: readonly string[];
  /** Eén of meer 2000-posities (meestal één; Hyades en Gienah hebben er twee). */
  readonly positions: readonly HdFixedStarPosition[];
  /** Side-nummer van de naamgenoot op exact dezelfde positie, indien van toepassing. */
  readonly sharesPositionWithSide?: number;
  /** Effect-classificatie uit het IHDS-positiedocument (ontbreekt voor een aantal sterren). */
  readonly effect?: HdFixedStarEffect;
}

const S = (
  side: number,
  key: string,
  name: string,
  altNames: readonly string[],
  positions: readonly HdFixedStarPosition[],
  extra?: { sharesPositionWithSide?: number; effect?: HdFixedStarEffect },
): HdFixedStarDefinition => ({ side, key, name, altNames, positions, ...extra });

const P = (
  sign: HdZodiacSign,
  degree: number,
  minute: number,
  gateLine: string,
  variantKey?: string,
): HdFixedStarPosition => (variantKey ? { sign, degree, minute, gateLine, variantKey } : { sign, degree, minute, gateLine });

export const HD_FIXED_STARS: readonly HdFixedStarDefinition[] = [
  S(1, 'difda', 'Difda', ['Deneb Kaitos'], [P('aries', 2, 35, '25.5')], { sharesPositionWithSide: 39, effect: 'unfortunate' }),
  S(2, 'pleiades', 'Pleiades', ['The Seven Sisters', 'Alcyone'], [P('gemini', 0, 0, '8.6-20.1')], { effect: 'unfortunate' }),
  S(3, 'hyades', 'Hyades', ['Hyadum I', 'Hyadum II'], [
    P('gemini', 5, 48, '16.1', 'hyadum-1'),
    P('gemini', 6, 52, '16.2', 'hyadum-2'),
  ], { effect: 'unfortunate' }),
  S(4, 'aldebaran', 'Aldebaran', ["Bull's North Eye"], [P('gemini', 9, 47, '16.5')], { effect: 'unfortunate' }),
  S(5, 'rigel', 'Rigel', ["Orion's Foot"], [P('gemini', 16, 50, '35.6')], { effect: 'fortunate' }),
  S(6, 'antares', 'Antares', ['Heart of the Scorpion'], [P('sagittarius', 9, 46, '9.5')], { effect: 'fortunate' }),
  S(7, 'rastaban', 'Rastaban', ['Alwaid', 'Beta Draconis'], [P('sagittarius', 11, 58, '5.1')], { effect: 'unfortunate' }),
  S(8, 'vega', 'Vega', [], [P('capricorn', 15, 19, '54.1')], { effect: 'fortunate' }),
  S(9, 'altair', 'Altair', ['The Eagle'], [P('aquarius', 1, 47, '60.6')], { effect: 'neutral' }),
  S(10, 'fomalhaut', 'Fomalhaut', [], [P('pisces', 3, 53, '55.5')], { effect: 'fortunate' }),
  S(11, 'deneb-adige', 'Deneb Adige', ['Alpha Cygni'], [P('pisces', 5, 20, '55.6')], { effect: 'fortunate' }),
  S(12, 'bellatrix', 'Bellatrix', ["Orion's Left Shoulder"], [P('gemini', 20, 57, '45.5')], { effect: 'unfortunate' }),
  S(13, 'capella', 'Capella', [], [P('gemini', 21, 52, '45.6')], { effect: 'fortunate' }),
  S(14, 'el-nath', 'El Nath', [], [P('gemini', 22, 35, '45.6')], { effect: 'fortunate' }),
  S(15, 'polaris', 'Polaris', [], [P('gemini', 28, 34, '15.1')], { sharesPositionWithSide: 16, effect: 'unfortunate' }),
  S(16, 'pole-star', 'The Pole Star', [], [P('gemini', 28, 34, '15.1')], { sharesPositionWithSide: 15, effect: 'unfortunate' }),
  S(17, 'betelgeuse', 'Betelgeuse', ["Orion's Right Shoulder"], [P('gemini', 28, 45, '15.1')], { effect: 'unfortunate' }),
  S(18, 'pisces', 'Pisces', ['Alpherg'], [P('aries', 26, 49, '3.1')]),
  S(19, 'canopus', 'Canopus', [], [P('cancer', 14, 58, '39.6')], { effect: 'unfortunate' }),
  S(20, 'castor', 'Castor', [], [P('cancer', 20, 14, '53.6')], { effect: 'unfortunate' }),
  S(21, 'pollux', 'Pollux', [], [P('cancer', 23, 13, '62.3')], { effect: 'unfortunate' }),
  S(22, 'procyon', 'Procyon', [], [P('cancer', 25, 47, '62.6')], { effect: 'unfortunate' }),
  S(23, 'serpentis', 'Serpentis', ['Unukalhai'], [P('scorpio', 22, 5, '43.4')], { effect: 'unfortunate' }),
  S(24, 'algenubi', 'Algenubi', [], [P('leo', 20, 42, '4.2')], { sharesPositionWithSide: 56, effect: 'neutral' }),
  S(25, 'regulus', 'Regulus', ["Lion's Heart"], [P('leo', 29, 50, '29.6')], { effect: 'fortunate' }),
  S(26, 'mizar', 'Mizar', [], [P('virgo', 15, 42, '64.5')], { effect: 'unfortunate' }),
  S(27, 'denebola', 'Denebola', [], [P('virgo', 21, 37, '47.6')], { effect: 'neutral' }),
  S(28, 'markeb', 'Markeb', [], [P('virgo', 28, 54, '46.1')]),
  S(29, 'algorab', 'Algorab', [], [P('libra', 13, 26, '48.5')], { effect: 'unfortunate' }),
  S(30, 'spica', 'Spica', ['The Fortunate One'], [P('libra', 23, 50, '32.4')], { effect: 'veryFortunate' }),
  S(31, 'dheneb', 'Dheneb', [], [P('capricorn', 19, 48, '54.5')], { effect: 'veryFortunate' }),
  S(32, 'hamal', 'Hamal', ['Alpha Arietis'], [P('taurus', 7, 40, '24.1')]),
  S(33, 'algedi', 'Algedi', ['Deneb Algedi'], [P('aquarius', 23, 33, '49.5')]),
  S(34, 'arcturus', 'Arcturus', [], [P('libra', 24, 14, '32.4')], { effect: 'veryFortunate' }),
  S(35, 'ras-alhague', 'Ras Alhague', [], [P('sagittarius', 22, 27, '26.6')], { effect: 'fortunate' }),
  S(36, 'mulam', 'Mulam: The Root', ['Shaula'], [P('sagittarius', 24, 35, '11.3')]),
  S(37, 'polis', 'Polis', [], [P('capricorn', 3, 13, '10.6')]),
  S(38, 'mirach', 'Mirach', [], [P('taurus', 0, 24, '3.5')], { effect: 'fortunate' }),
  S(39, 'deneb-kaitos', 'Deneb Kaitos', ['Difda'], [P('aries', 2, 35, '25.5')], { sharesPositionWithSide: 1, effect: 'unfortunate' }),
  S(40, 'agena', 'Agena', ['Beta Centauri'], [P('scorpio', 23, 48, '43.6')]),
  S(41, 'spiculum', 'Spiculum', [], [P('capricorn', 1, 4, '10.4')], { effect: 'unfortunate' }),
  S(42, 'talitha', 'Talitha', [], [P('leo', 2, 48, '31.2')], { effect: 'neutral' }),
  S(43, 'almach', 'Almach', [], [P('taurus', 14, 14, '2.2')], { effect: 'fortunate' }),
  S(44, 'rana', 'Rana', [], [P('taurus', 20, 6, '23.2')], { effect: 'fortunate' }),
  S(45, 'caput-algol', 'Caput Algol', ["The Gorgon's Head", 'Algol'], [P('taurus', 26, 10, '8.2')], { effect: 'veryUnfortunate' }),
  S(46, 'alphard', 'Alphard', [], [P('leo', 27, 17, '29.3')], { effect: 'unfortunate' }),
  S(47, 'al-jabhah', 'Al Jabhah', [], [P('leo', 27, 54, '29.4')], { effect: 'neutral' }),
  S(48, 'benetnash', 'Benetnash', ['Alkaid'], [P('virgo', 26, 56, '6.5')]),
  S(49, 'caput-hercules', 'Caput Hercules', ['Ras Algethi'], [P('sagittarius', 16, 9, '5.6')]),
  S(50, 'alpheratz', 'Alpheratz', ['Alpha Andromedae'], [P('aries', 14, 18, '21.6')]),
  S(51, 'algenib', 'Algenib', [], [P('aries', 9, 9, '17.6')], { effect: 'unfortunate' }),
  S(52, 'phact', 'Phact', [], [P('gemini', 22, 10, '45.6')], { effect: 'fortunate' }),
  S(53, 'alphecca', 'Alphecca', [], [P('scorpio', 12, 18, '44.5')], { effect: 'fortunate' }),
  S(54, 'acubens', 'Acubens', [], [P('leo', 13, 38, '7.1')], { effect: 'fortunate' }),
  S(55, 'merak', 'Merak', [], [P('leo', 19, 26, '4.1')], { effect: 'fortunate' }),
  S(56, 'ras-elased-aust', 'Ras Elased Aust', [], [P('leo', 20, 42, '4.2')], { sharesPositionWithSide: 24, effect: 'neutral' }),
  S(57, 'thuban', 'Thuban', [], [P('virgo', 7, 27, '40.2')], { effect: 'fortunate' }),
  S(58, 'terebellum', 'Terebellum', [], [P('capricorn', 25, 51, '61.6')], { effect: 'fortunate' }),
  S(59, 'albali', 'Albali', [], [P('aquarius', 11, 43, '19.5')], { effect: 'neutral' }),
  S(60, 'nashira', 'Nashira', [], [P('aquarius', 21, 47, '49.4')], { effect: 'fortunate' }),
  S(61, 'gienah', 'Gienah', ['Giena'], [
    P('libra', 10, 43, '48.2', 'corvus'),
    P('aquarius', 27, 44, '30.4', 'cygnus'),
  ], { effect: 'neutral' }),
  S(62, 'sadalsuud', 'Sadalsuud', [], [P('aquarius', 23, 24, '49.5')], { effect: 'fortunate' }),
  S(63, 'alnasl', 'Alnasl', [], [P('capricorn', 1, 16, '10.4')], { effect: 'unfortunate' }),
  S(64, 'zuben-algenubi', 'Zuben Algenubi', ['South Scale'], [P('scorpio', 15, 5, '1.2')], { effect: 'unfortunate' }),
  S(65, 'kornephoros', 'Kornephoros', ['Beta Herculis'], [P('sagittarius', 1, 6, '34.2')]),
  S(66, 'achernar', 'Achernar', [], [P('pisces', 15, 19, '63.5')]),
];

export function getHdFixedStarBySide(side: number): HdFixedStarDefinition | undefined {
  return HD_FIXED_STARS.find(star => star.side === side);
}

const STAR_BY_KEY = new Map(HD_FIXED_STARS.map(star => [star.key, star]));

export function getHdFixedStarByKey(key: string): HdFixedStarDefinition | undefined {
  return STAR_BY_KEY.get(key);
}
